import React from "react";
import { readiness, profileReady } from "./cqcProfileModel";
import {
  countryOf,
  addressReady,
  groupsForSites,
  frameworkLabel,
} from "./cqcFrameworks";
import { pctBg, fmtPct } from "./cqcColors";
export function ProgressBar({ stats }) {
  const total = stats.total || 1;
  return (
    <div
      className="cqc-stack"
      role="img"
      aria-label={`${stats.completed} reviewed, ${stats.ongoing} in progress, ${stats.notStarted} not started out of ${stats.total} required`}
    >
      <span
        className="done"
        style={{ width: `${(stats.completed / total) * 100}%` }}
      />
      <span
        className="ongoing"
        style={{ width: `${(stats.ongoing / total) * 100}%` }}
      />
      <span
        className="todo"
        style={{ width: `${(stats.notStarted / total) * 100}%` }}
      />
    </div>
  );
}
export default function CqcProgress({ rows, sites, onSelect }) {
  const stats = readiness(rows),
    pending = sites.filter((s) => !profileReady(s)).length,
    missingAddress = sites.filter((s) => !addressReady(s)).length;
  const reviewDue = sites.filter(
    (s) =>
      s.serviceProfile?.reviewDate &&
      s.serviceProfile.reviewDate < new Date().toISOString().slice(0, 10),
  ).length;
  const nations = [
    ...new Set(sites.map((s) => countryOf(s) || "Address needed")),
  ];
  return (
    <section>
      <div className="cqc-stats">
        {[
          [
            "Evidence readiness",
            stats.pct == null ? "—" : fmtPct(stats.pct),
            "#389e0d",
            `${stats.completed} / ${stats.total} required items reviewed`,
          ],
          [
            "In progress",
            stats.ongoing,
            "#faad14",
            `${stats.notStarted} not started`,
          ],
          [
            "Sources configured",
            `${stats.configured} / ${stats.total}`,
            "#1677ff",
            `${stats.supporting} supporting items available`,
          ],
          [
            "Service profiles",
            `${sites.length - pending} / ${sites.length}`,
            pending ? "#cf1322" : "#389e0d",
            `${pending} awaiting confirmation · ${reviewDue} review due`,
          ],
        ].map(([label, value, color, sub]) => (
          <div
            className="cqc-card cqc-stat"
            style={{ borderTopColor: color }}
            key={label}
          >
            <div className="cqc-muted">{label}</div>
            <div className="cqc-number">{value}</div>
            <div className="cqc-muted">{sub}</div>
          </div>
        ))}
      </div>
      <ProgressBar stats={stats} />
      <p className="cqc-muted">
        Green: reviewed · Amber: in progress · Red: not started.{" "}
        {stats.excluded} not applicable. Combined evidence readiness across
        national plans; not a regulatory rating or a comparison of national
        grading systems.
      </p>
      {pending > 0 && (
        <p className="cqc-warning">
          {pending} organisation{pending === 1 ? "" : "s"} still need a
          confirmed address or service profile ({missingAddress} address
          {missingAddress === 1 ? "" : "es"} needed). Their previous evidence is
          retained but excluded from the percentage until the correct national
          plan is confirmed.
        </p>
      )}
      {nations.map((nation) => {
        const ns = sites.filter(
            (s) => (countryOf(s) || "Address needed") === nation,
          ),
          groups = groupsForSites(ns);
        return (
          <details
            className="cqc-card"
            key={nation}
            open={nations.length === 1 ? true : undefined}
          >
            <summary>
              <strong>{nation}</strong> · {ns.length} organisation
              {ns.length === 1 ? "" : "s"}
            </summary>
            <div className="cqc-heatmap">
              <table>
                <thead>
                  <tr>
                    <th>Organisation / framework</th>
                    {groups.map((g) => (
                      <th key={g.id}>
                        {g.regulator} · {g.label}
                      </th>
                    ))}
                    <th>Overall</th>
                  </tr>
                </thead>
                <tbody>
                  {ns.map((site) => (
                    <tr key={site._id}>
                      <th>
                        {onSelect ? (
                          <button onClick={() => onSelect(site._id)}>
                            {site.name}
                          </button>
                        ) : (
                          site.name
                        )}
                        <div className="cqc-muted">{frameworkLabel(site)}</div>
                      </th>
                      {[...groups, { id: "all" }].map((g) => {
                        const rr = rows.filter(
                            (r) =>
                              r.site._id === site._id &&
                              (g.id === "all" || r.group.id === g.id),
                          ),
                          r = readiness(rr),
                          ready = profileReady(site);
                        return (
                          <td
                            key={g.id}
                            style={{
                              background:
                                ready && rr.length ? pctBg(r.pct) : "#f5f5f5",
                            }}
                          >
                            {!addressReady(site) ? (
                              "Address needed"
                            ) : !ready ? (
                              "Profile needed"
                            ) : !rr.length ? (
                              "Different framework"
                            ) : r.pct == null ? (
                              "No required items"
                            ) : (
                              <>
                                <strong>{fmtPct(r.pct)}</strong>
                                <div>
                                  {r.completed}/{r.total} reviewed
                                </div>
                              </>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        );
      })}
    </section>
  );
}
